// The tablet's right rail: what a phone tucks into sheets, kept in view where
// the width allows it — what is assigned to a leader, the buffers feeding the
// station, what has been finished here today, and who is on shift.

import { AvatarTile } from '@/components/ui/misc';
import { NeedsYouList } from '@/components/home/NeedsYou';
import { employeeAvatarUrl } from '@/api/client';
import type { BufferLineVM, NeedsYouVM } from '@/api/types';
import { useT } from '@/i18n/useT';
import { bufferTone } from '@/screens/leader/FloorBuffers';

export function RightRail({
  isLeader,
  mine,
  onOpenNeedsYouItem,
  stationName,
  buffers,
  completedCount,
  completedText,
  crew,
  onOpenCrew,
}: {
  isLeader: boolean;
  /** Andons owned, alerts raised, to-dos due — the leader's own list. */
  mine: NeedsYouVM[];
  onOpenNeedsYouItem: (item: NeedsYouVM) => void;
  stationName: string;
  buffers: BufferLineVM[];
  completedCount: number;
  completedText: string;
  crew: Array<{ id: number; name: string }>;
  onOpenCrew: () => void;
}) {
  const t = useT();
  return (
    <aside
      style={{
        flex: 'none',
        width: 300,
        borderLeft: '1px solid var(--line)',
        background: 'var(--surface)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowY: 'auto',
      }}
    >
      {isLeader && (
        <section aria-label={t.assignedToMeT}>
          <div className="sectionLabel">
            {t.assignedToMeT} · {mine.length}
          </div>
          <NeedsYouList items={mine} compact onOpen={onOpenNeedsYouItem} emptyText={t.nothingAssignedT} />
        </section>
      )}

      <section aria-label={t.buffersFeedingT}>
        <div className="sectionLabel">
          {t.buffersFeedingT} {stationName.replace(/^FL - /, '')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {buffers.map((b) => {
            const tone = bufferTone(b.state, t);
            const pct = b.max ? Math.min(100, Math.round((b.filled / b.max) * 100)) : 0;
            return (
              <div
                key={b.id}
                style={{ background: 'var(--bg)', borderRadius: 12, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ font: "700 10.5px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>
                    {b.flowlineName.replace(/^Flowline\s*/i, 'FL ')}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      font: '700 12px Manrope,sans-serif',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.inWorkcentreName}
                  </span>
                  <span style={{ font: '700 10px Manrope,sans-serif', color: tone.colour }}>{tone.label}</span>
                </div>
                <span style={{ height: 6, borderRadius: 3, background: 'var(--line)', overflow: 'hidden', display: 'block' }}>
                  <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: tone.colour }} />
                </span>
              </div>
            );
          })}
          {buffers.length === 0 && (
            <div style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.noBufferIntoT}</div>
          )}
        </div>
      </section>

      <section aria-label={t.completedHereTodayT}>
        <div className="sectionLabel">
          {t.completedHereTodayT} · {completedCount}
        </div>
        <div style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', lineHeight: 1.5 }}>{completedText}</div>
      </section>

      <button
        type="button"
        className="press"
        onClick={onOpenCrew}
        style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, textAlign: 'left' }}
      >
        <span style={{ display: 'flex', paddingLeft: 8 }}>
          {crew.slice(0, 4).map((c) => (
            <AvatarTile
              key={c.id}
              name={c.name}
              size={26}
              round
              photoUrl={employeeAvatarUrl(c.id)}
              style={{ marginLeft: -8, border: '2px solid var(--surface)' }}
            />
          ))}
        </span>
        <span style={{ font: '600 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>
          {crew.length} {t.onShiftNowT} ›
        </span>
      </button>
    </aside>
  );
}
